/**
 * SettingsDrawer — right-side slide-in drawer. Theme + language pickers,
 * View statistics, How to play, Restore purchase, Reset progress, About.
 */
import type { Locale, ThemeMode } from './types';

interface Props {
  readonly open: boolean;
  readonly theme: ThemeMode;
  readonly locale: Locale;
  readonly onClose: () => void;
  readonly onThemeChange: (t: ThemeMode) => void;
  readonly onLocaleChange: (l: Locale) => void;
  readonly onOpenStats: () => void;
  readonly onOpenTutorial: () => void;
  readonly onRestorePurchase: () => void;
  readonly onResetProgress: () => void;
}

const THEMES: readonly { key: ThemeMode; label: string }[] = [
  { key: 'auto', label: 'Auto' },
  { key: 'light', label: 'Light' },
  { key: 'dark', label: 'Dark' },
];

const LOCALES: readonly { key: Locale; label: string }[] = [
  { key: 'en', label: 'EN' },
  { key: 'fr', label: 'FR' },
  { key: 'de', label: 'DE' },
];

export function SettingsDrawer(props: Props) {
  const {
    theme, locale, onClose,
    onThemeChange, onLocaleChange,
    onOpenStats, onOpenTutorial,
    onRestorePurchase, onResetProgress,
  } = props;

  return (
    <aside id="settingsDrawer" className="drawer">
      <div className="scrim" onClick={onClose}></div>
      <div className="drawerPanel">
        <header>
          <h2>Settings</h2>
          <button className="iconBtn ghost" aria-label="Close" onClick={onClose}>×</button>
        </header>
        <div className="drawerBody">
          <div className="settingRow">
            <div className="label">Appearance</div>
            <div className="seg" role="radiogroup" aria-label="Theme">
              {THEMES.map((t) => (
                <button
                  key={t.key}
                  className={t.key === theme ? 'active' : ''}
                  onClick={() => onThemeChange(t.key)}
                >{t.label}</button>
              ))}
            </div>
          </div>
          <div className="settingRow">
            <div className="label">Language</div>
            <div className="seg" role="radiogroup" aria-label="Language">
              {LOCALES.map((l) => (
                <button
                  key={l.key}
                  className={l.key === locale ? 'active' : ''}
                  onClick={() => onLocaleChange(l.key)}
                >{l.label}</button>
              ))}
            </div>
          </div>
          <hr />
          <button className="settingLink" onClick={onOpenStats}>View statistics →</button>
          <button className="settingLink" onClick={onOpenTutorial}>How to play →</button>
          <hr />
          <button className="settingLink" onClick={onRestorePurchase}>Restore purchase</button>
          <button className="settingLink danger" onClick={onResetProgress}>Reset progress…</button>
          <hr />
          <div className="settingRow about">
            <div className="muted">Doublet</div>
            <div className="muted small">
              A quiet word-ladder puzzle from Quiet Paper Studio.
              <br />Carroll's mechanic, 1879.
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
